import { useEffect, useRef, useState } from 'react';

/**
 * List pages: scrolling first moves the header and cards away until the table card reaches the top,
 * then scrolls the table rows. Put `pageRef` and `data-table-locked` on the page's scroll container;
 * the table's scroll area stops scrolling while it is locked, so the wheel moves the page instead.
 */
export function useTableFirstScroll() {
  const pageRef = useRef<HTMLDivElement>(null);
  const [tableLocked, setTableLocked] = useState(false);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return;

    // Locked until the page is scrolled to the bottom, i.e. the table fills the screen.
    const update = () => setTableLocked(page.scrollTop < page.scrollHeight - page.clientHeight - 1);
    update();

    page.addEventListener('scroll', update, { passive: true });
    // Update on the next frame: changing layout inside the observer callback makes the browser
    // report "ResizeObserver loop completed with undelivered notifications".
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    });
    observer.observe(page);
    return () => {
      page.removeEventListener('scroll', update);
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return { pageRef, tableLocked };
}
